import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import {
  MagnifyingGlassIcon,
  BellIcon,
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  SunIcon,
  MoonIcon,
  CogIcon,
  UserIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon
} from '@heroicons/react/24/outline'
import { apiService } from '../services/apiService'
import toast from 'react-hot-toast'

const Header = ({ onToggleSidebar, onToggleSidebarMobile }) => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [hasNewNotification, setHasNewNotification] = useState(false)
  
  const previousUnreadCount = useRef(0)
  const pollingInterval = useRef(null)

  useEffect(() => {
    loadNotifications()
    startPolling()
    
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current)
      }
    }
  }, [])

  const startPolling = () => {
    pollingInterval.current = setInterval(() => {
      loadNotifications(true)
    }, 30000)
  }

  const loadNotifications = async (silent = false) => {
    try {
      const response = await apiService.getNotifications({ limit: 10 })
      const newNotifications = response.notifications || []
      const newUnreadCount = response.unread_count || 0
      
      setNotifications(newNotifications)
      
      if (previousUnreadCount.current > 0 && newUnreadCount > previousUnreadCount.current) {
        setHasNewNotification(true)
        
        const latestNotification = newNotifications.find(n => !n.read)
        if (latestNotification) {
          showNotificationToast(latestNotification)
        }
        
        setTimeout(() => setHasNewNotification(false), 3000)
      }
      
      setUnreadCount(newUnreadCount)
      previousUnreadCount.current = newUnreadCount
      
    } catch (error) {
      if (!silent) {
        console.error('Erro ao carregar notificações:', error)
      }
    }
  }

  const showNotificationToast = (notification) => {
    const getIcon = (priority) => {
      if (priority === 'urgent') return '🚨'
      if (priority === 'high') return '⚠️'
      return '📢'
    }

    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'}
                      max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto
                      flex ring-1 ring-black ring-opacity-5 border-l-4 ${
                        notification.priority === 'urgent' ? 'border-danger-500' :
                        notification.priority === 'high' ? 'border-secondary-500' :
                        'border-primary-500'
                      }`}
           onClick={() => {
             toast.dismiss(t.id)
             navigate('/notifications')
           }}
           style={{ cursor: 'pointer' }}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 text-2xl">
              {getIcon(notification.priority)}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {notification.title}
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {notification.message}
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200 dark:border-gray-700">
          <button
            onClick={(e) => {
              e.stopPropagation()
              toast.dismiss(t.id)
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-500 focus:outline-none">
            ✕
          </button>
        </div>
      </div>
    ), {
      duration: 5000,
      position: 'top-right',
      style: {
        background: 'transparent',
        boxShadow: 'none'
      }
    })
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      toast(`Buscando por: ${searchQuery}`, {
      icon: 'ℹ️',
    })
    }
  }

  const markAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsRead()
      setUnreadCount(0)
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      toast.success('Todas marcadas como lidas')
    } catch (error) {
      console.error('Erro ao marcar notificações:', error)
      toast.error('Erro ao marcar notificações')
    }
  }

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await apiService.markNotificationRead(notification.id)
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      } catch (error) {
        console.error('Erro ao marcar como lida:', error)
      }
    }
    
    setShowNotifications(false)
    if (notification.action_url) {
      navigate(notification.action_url)
    } else {
      navigate('/notifications')
    }
  }

  const handleLogout = () => {
    logout()
    setShowProfile(false)
  }

  const handleEmailClick = async () => {
    try {
      const response = await apiService.createAutologinToken()
      if (response.success && response.url) {
        // Se não tiver SSO, mostrar mensagem informativa
        if (response.sso === false && response.message) {
          toast(response.message, {
            icon: 'ℹ️',
            duration: 3000
          })
        }
        window.open(response.url, '_blank')
      } else {
        toast.error('Erro ao gerar link de acesso ao webmail')
      }
    } catch (error) {
      console.error('Erro ao acessar webmail:', error)
      toast.error('Erro ao acessar webmail. Tente novamente.')
    }
  }

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 lg:px-6 py-3 lg:py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Hamburger menu - apenas no mobile quando não há bottom nav visível */}
          <button
            onClick={onToggleSidebarMobile}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <Bars3Icon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
          </button>

          {/* Logo/Título no mobile */}
          <div className="lg:hidden">
            <h1 className="text-lg font-bold text-viploc-navy dark:text-white">VIPLOC</h1>
          </div>

          {/* Campo de busca - escondido no mobile */}
          <form onSubmit={handleSearch} className="relative hidden lg:block">
            <div className="flex items-center">
              <MagnifyingGlassIcon className="absolute left-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar veículos, clientes, contratos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-64 lg:w-96 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              />
            </div>
          </form>
        </div>

        <div className="flex items-center space-x-2 lg:space-x-4">
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <BellIcon className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform ${
                hasNewNotification ? 'animate-bounce' : ''
              }`} />
              {unreadCount > 0 && (
                <span className={`absolute -top-1 -right-1 w-5 h-5 bg-danger-500 text-white text-xs
                               rounded-full flex items-center justify-center ${
                                 hasNewNotification ? 'animate-pulse scale-110' : ''
                               }`}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg
                            border border-gray-200 dark:border-gray-700 z-[2000] max-h-[600px] overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Notificações
                    </h3>
                    <div className="flex items-center space-x-2">
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                          Marcar todas
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowNotifications(false)
                          navigate('/notifications')
                        }}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                        Ver todas
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-y-auto flex-1">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700
                                  transition-colors cursor-pointer ${
                          !notification.read ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                        }`}>
                        <div className="flex items-start space-x-3">
                          <div className="text-lg flex-shrink-0">
                            {notification.priority === 'urgent' ? '🚨' : 
                             notification.priority === 'high' ? '⚠️' :
                             notification.type === 'success' ? '✅' : '📢'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                              {notification.title}
                              {!notification.read && (
                                <span className="ml-2 w-2 h-2 bg-secondary-500 rounded-full animate-pulse"></span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                              {new Date(notification.created_at).toLocaleString('pt-BR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      <BellIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma notificação</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Botão de Email Corporativo - escondido no mobile */}
          <button
            onClick={handleEmailClick}
            className="hidden lg:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Webmail Corporativo">
            <EnvelopeIcon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
          </button>

          {/* Chat - escondido no mobile */}
          <button className="hidden lg:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative">
            <ChatBubbleLeftIcon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></span>
          </button>

          {/* Tema - escondido no mobile */}
          <button
            onClick={toggleTheme}
            className="hidden lg:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            {isDark ? (
              <SunIcon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            ) : (
              <MoonIcon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            )}
          </button>

          {/* Configurações - escondido no mobile */}
          <button className="hidden lg:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <CogIcon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
          </button>

          <div className="relative">
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center space-x-2 lg:space-x-3 p-1 lg:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center shadow-orange">
                <span className="text-sm font-medium text-white">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {user?.role}
                </p>
              </div>
              <svg className="hidden lg:block w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {showProfile && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg
                            border border-gray-200 dark:border-gray-700 z-[2000]">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center shadow-orange">
                      <span className="text-lg font-medium text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{user?.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                      <span className="inline-block px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900
                                     text-primary-800 dark:text-primary-300 rounded-full capitalize mt-1">
                        {user?.role}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-2">
                  <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 
                                   hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <UserIcon className="w-4 h-4" />
                    <span>Meu Perfil</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 
                                   hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <CogIcon className="w-4 h-4" />
                    <span>Configurações</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 
                                   hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <QuestionMarkCircleIcon className="w-4 h-4" />
                    <span>Ajuda & Suporte</span>
                  </button>
                  <hr className="my-2 border-gray-200 dark:border-gray-600" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-danger-600 dark:text-danger-400
                             hover:bg-danger-50 dark:hover:bg-danger-900/50 rounded-lg transition-colors">
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    <span>Sair do Sistema</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(showNotifications || showProfile) && (
        <div
          className="fixed inset-0 z-[1999]"
          onClick={() => {
            setShowNotifications(false)
            setShowProfile(false)
          }}
        />
      )}
    </header>
  )
}

export default Header